import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './GeneralInfoForm.css'; // Reuse the same CSS for floating labels
import { getReligion, getCaste, getBloodGroup } from './../api/EmpMasterApi';

const PersonalInfo = forwardRef((props, ref) => {
     // Use lifted state if provided, else local state
     const [localFormData, localSetFormData] = useState({
          mobile: '',
          emergency_contact: '',
          email: '',
          dob: '',
          gender: '',
          father_husband_name: '',
          marital_status: '',
          blood_group: '',
          religion: '',
          caste: '',
     });
     // Use props data if available, otherwise use local state
     const formData = props.formData || localFormData;
     const setFormData = props.setFormData || localSetFormData;

     // Debug: Log form data changes
     useEffect(() => {
          console.log('📋 PersonalInfo formData updated:', formData);
     }, [formData]);

     // Validation state
     const [validationErrors, setValidationErrors] = useState({});

     // Expose validation method to parent component
     useImperativeHandle(ref, () => ({
          validateForm: () => {
               const errors = {};

               // Check required fields
               if (!formData.email?.trim()) {
                    errors.email = 'Email is required';
               } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
                    errors.email = 'Please enter a valid email address';
               }
               if (!formData.mobile?.trim()) {
                    errors.mobile = 'Mobile number is required';
               } else if (!/^\d{10}$/.test(formData.mobile.replace(/\D/g, ''))) {
                    errors.mobile = 'Please enter a valid 10-digit mobile number';
               }
               if (!formData.gender) {
                    errors.gender = 'Gender is required';
               }

               setValidationErrors(errors);
               return Object.keys(errors).length === 0;
          },
          clearValidation: () => {
               setValidationErrors({});
          }
     }));

     // Clear individual field validation when user starts typing
     const clearFieldError = (fieldName) => {
          if (validationErrors[fieldName]) {
               setValidationErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[fieldName];
                    return newErrors;
               });
          }
     };

     const [dropdowns, setDropdowns] = useState({
          bloodGroups: [],
          religions: [],
          castes: [],
     });
     const [failed, setFailed] = useState({
          religions: false,
          castes: false,
          bloodGroups: false,
     });
     // Helper to fetch a single dropdown
     const fetchSingleDropdown = useCallback(async (type) => {
          try {
               let result;
               if (type === 'religions') result = await getReligion();
               if (type === 'castes') result = await getCaste();
               if (type === 'bloodGroups') result = await getBloodGroup();

               setDropdowns(prev => ({
                    ...prev,
                    [type]: result?.data || [],
               }));
               setFailed(prev => ({
                    ...prev,
                    [type]: false,
               }));
          } catch {
               setFailed(prev => ({
                    ...prev,
                    [type]: true,
               }));
          }
     }, []);

     // Replace the existing fetchDropdowns function with this improved version:

     // Fetch all dropdowns initially
     const fetchDropdowns = useCallback(async () => {
          const results = await Promise.allSettled([
               getReligion(),
               getCaste(),
               getBloodGroup(),
          ]);
          setDropdowns({
               religions: results[0].status === 'fulfilled' && results[0].value?.data ? results[0].value.data : [],
               castes: results[1].status === 'fulfilled' && results[1].value?.data ? results[1].value.data : [],
               bloodGroups: results[2].status === 'fulfilled' && results[2].value?.data ? results[2].value.data : [],
          });
          setFailed({
               religions: results[0].status !== 'fulfilled',
               castes: results[1].status !== 'fulfilled',
               bloodGroups: results[2].status !== 'fulfilled',
          });
     }, []);

     useEffect(() => {
          fetchDropdowns();
     }, [fetchDropdowns]);

     // Retry handler for failed dropdowns
     const handleRetry = (type) => {
          fetchSingleDropdown(type);
     };
     const [loading, setLoading] = useState({
          bloodGroups: true,
          religions: true,
          castes: true,
     });
     // Fetch all dropdowns immediately on mount
     useEffect(() => {
          console.log('🚀 Loading PersonalInfo dropdowns...');

          // Fetch religions immediately
          getReligion()
               .then(response => {
                    setDropdowns(prev => ({ ...prev, religions: response?.data || [] }));
                    setLoading(prev => ({ ...prev, religions: false }));
                    console.log('✅ Religions loaded');
               })
               .catch(error => {
                    console.error('❌ Failed to load religions:', error);
                    setLoading(prev => ({ ...prev, religions: false }));
               });

          // Fetch castes immediately
          getCaste()
               .then(response => {
                    setDropdowns(prev => ({ ...prev, castes: response?.data || [] }));
                    setLoading(prev => ({ ...prev, castes: false }));
                    console.log('✅ Castes loaded');
               })
               .catch(error => {
                    console.error('❌ Failed to load castes:', error);
                    setLoading(prev => ({ ...prev, castes: false }));
               });

          // Fetch blood groups immediately
          getBloodGroup()
               .then(response => {
                    setDropdowns(prev => ({ ...prev, bloodGroups: response?.data || [] }));
                    setLoading(prev => ({ ...prev, bloodGroups: false }));
                    console.log('✅ Blood groups loaded');
               })
               .catch(error => {
                    console.error('❌ Failed to load blood groups:', error);
                    setLoading(prev => ({ ...prev, bloodGroups: false }));
               });
     }, []); // Empty dependency array - run once on mount

     // Enhanced onChange handler
     const handleInputChange = (fieldName, value) => {
          const updatedData = { ...formData, [fieldName]: value };
          setFormData(updatedData);
          clearFieldError(fieldName);
     };
     return (
          <div className="custom_form_wrapper">
               <div className="form-grid">
                    <div className="form-floating">
                         <input
                              type="text"
                              className={`form-control ${validationErrors.mobile ? 'is-invalid' : ''}`}
                              name="mobile"
                              id="mobile"
                              placeholder=" "
                              value={formData.mobile}
                              onChange={(e) => handleInputChange('mobile', e.target.value)}
                              autoComplete="off"
                         />
                         <label htmlFor="mobile">Mobile</label>
                         {validationErrors.mobile && (
                              <div className="invalid-feedback d-block">
                                   <small><i className="fas fa-exclamation-circle me-1"></i>{validationErrors.mobile}</small>
                              </div>
                         )}
                    </div>
                    <div className="form-floating">
                         <input
                              type="text"
                              className={`form-control ${validationErrors.emergency_contact ? 'is-invalid' : ''}`}
                              name="emergency_contact"
                              id="emergency_contact"
                              placeholder=" "
                              value={formData.emergency_contact}
                              onChange={(e) => handleInputChange('emergency_contact', e.target.value)}
                              autoComplete="off"
                         />
                         <label htmlFor="emergency_contact">Emergency Contact</label>
                         {validationErrors.emergency_contact && (
                              <div className="invalid-feedback d-block">
                                   <small><i className="fas fa-exclamation-circle me-1"></i>{validationErrors.emergency_contact}</small>
                              </div>
                         )}
                    </div>
                    <div className="form-floating">
                         <input
                              type="email"
                              className={`form-control ${validationErrors.email ? 'is-invalid' : ''}`}
                              name="email"
                              id="email"
                              placeholder=" "
                              value={formData.email ?? ''} // <-- Ensure fallback to empty string
                              onChange={e => {
                                   handleInputChange('email', e.target.value); // Use the same handler as other fields
                              }}
                              autoComplete="off"
                         />
                         <label htmlFor="email">Email</label>
                         {validationErrors.email && (
                              <div className="invalid-feedback d-block">
                                   <small><i className="fas fa-exclamation-circle me-1"></i>{validationErrors.email}</small>
                              </div>
                         )}
                    </div>
                    <div className="row align-items-center">
                         <div className="col-auto">
                              <label style={{ fontSize: '14px', marginLeft: '5px' }} htmlFor="dob" className="form-label mb-0">Date of Birth</label>
                         </div>
                         <div className="col">
                              <input
                                   type="date"
                                   className="form-control form-control-sm"
                                   name="dob"
                                   id="dob"
                                   value={formData.dob || ''}
                                   onChange={e => handleInputChange('dob', e.target.value)}
                                   max={(() => {
                                        const d = new Date();
                                        d.setFullYear(d.getFullYear() - 18);
                                        return d.toISOString().split('T')[0];
                                   })()}
                                   autoComplete="off"
                              />
                         </div>
                    </div>
                    <div className="form-floating">
                         <select
                              className={`form-select ${validationErrors.gender ? 'is-invalid' : ''}`}
                              name="gender"
                              id="gender"
                              value={formData.gender ?? ''}
                              onChange={(e) => {
                                   setFormData({ ...formData, gender: e.target.value });
                                   clearFieldError('gender');
                              }}
                         >
                              <option value="" disabled hidden>Gender</option>
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                              <option value="Other">Other</option>
                         </select>
                         <label htmlFor="gender" style={{ opacity: formData.gender ? 1 : 0 }}>
                              Gender
                         </label>
                         {validationErrors.gender && (
                              <div className="invalid-feedback d-block">
                                   <small><i className="fas fa-exclamation-circle me-1"></i>{validationErrors.gender}</small>
                              </div>
                         )}
                    </div>
                    <div className="form-floating">
                         <input
                              type="text"
                              className="form-control"
                              name="father_husband_name"
                              id="father_husband_name"
                              placeholder=" "
                              value={formData.father_husband_name ?? ''}
                              onChange={e => setFormData({ ...formData, father_husband_name: e.target.value })}
                              autoComplete="off"
                         />
                         <label htmlFor="father_husband_name">Father / Husband Name</label>
                    </div>
                    <div className="form-floating">
                         <select
                              className="form-select"
                              name="marital_status"
                              id="marital_status"
                              value={formData.marital_status ?? ''}
                              onChange={e => setFormData({ ...formData, marital_status: e.target.value })}
                         >
                              <option value="" disabled hidden>Marital Status</option>
                              <option value="Single">Single</option>
                              <option value="Married">Married</option>
                              <option value="Divorced">Divorced</option>
                              <option value="Widowed">Widowed</option>
                         </select>
                         <label htmlFor="marital_status" style={{ opacity: formData.marital_status ? 1 : 0 }}>Marital Status</label>
                    </div>
                    <div className="form-floating">
                         <select
                              className="form-select"
                              name="blood_group"
                              id="blood_group"
                              value={formData.blood_group ?? ''}
                              onChange={e => setFormData({ ...formData, blood_group: e.target.value })}
                              required
                         >
                              <option value="" disabled hidden>
                                   {loading.bloodGroups ? 'Loading blood groups...' : 'Blood Group'}
                              </option>
                              {/* Show current value first if it exists and not in dropdown yet */}
                              {formData.blood_group && !dropdowns.bloodGroups.find(bg => bg.bloodgroup === formData.blood_group) && (
                                   <option value={formData.blood_group}>{formData.blood_group}</option>
                              )}
                              {dropdowns.bloodGroups.map(bg => (
                                   <option key={bg.id} value={bg.bloodgroup}>
                                        {bg.bloodgroup}
                                   </option>
                              ))}
                         </select>
                         <label htmlFor="blood_group" style={{ opacity: formData.blood_group ? 1 : 0 }}>Blood Group</label>
                    </div>
                    <div className="form-floating">
                         <select
                              className="form-select"
                              name="religion"
                              id="religion"
                              value={formData.religion ?? ''}
                              onChange={e => setFormData({ ...formData, religion: e.target.value })}
                              required
                         >
                              <option value="" disabled hidden>
                                   {loading.religions ? 'Loading religions...' : 'Religion'}
                              </option>
                              {/* Show current value first if it exists and not in dropdown yet */}
                              {formData.religion && !dropdowns.religions.find(r => r.religion === formData.religion) && (
                                   <option value={formData.religion}>{formData.religion}</option>
                              )}
                              {dropdowns.religions.map(religion => (
                                   <option key={religion.id} value={religion.religion}>
                                        {religion.religion}
                                   </option>
                              ))}
                         </select>
                         <label htmlFor="religion" style={{ opacity: formData.religion ? 1 : 0 }}>Religion</label>
                    </div>
                    <div className="form-floating">
                         <select
                              className="form-select"
                              name="caste"
                              id="caste"
                              value={formData.caste ?? ''}
                              onChange={e => setFormData({ ...formData, caste: e.target.value })}
                              required
                         >
                              <option value="" disabled hidden>
                                   {loading.castes ? 'Loading castes...' : 'Caste'}
                              </option>
                              {/* Show current value first if it exists and not in dropdown yet */}
                              {formData.caste && !dropdowns.castes.find(c => c.caste === formData.caste) && (
                                   <option value={formData.caste}>{formData.caste}</option>
                              )}
                              {dropdowns.castes.map(caste => (
                                   <option key={caste.id} value={caste.caste}>
                                        {caste.caste}
                                   </option>
                              ))}
                         </select>
                         <label htmlFor="caste" style={{ opacity: formData.caste ? 1 : 0 }}>Caste</label>
                    </div>
               </div>
          </div>
     );
});

export default PersonalInfo;